import { html, nothing, render } from "lit-html";
import { classMap } from "lit-html/directives/class-map.js";
import {
	googletag,
	registerRenderCallback,
	unregisterRenderCallbacks,
} from "./loader";
import { translate } from "@escapio/shared/l10n/translate";
import slots from "./Slots";
import { stopCheck, visible } from "@escapio/shared/event/visibility-observer";
import { getMinWidth, getVersion } from "@escapio/portal/dom/screen";

const REFRESH_INTERVAL_SECONDS = 10;

customElements.define(
	"esc-google-publisher-ad-container",
	class extends HTMLElement {
		constructor() {
			super();
			this.isEmpty = true;
			this.googleSlot = null;
			this.isLoaded = false;
			this.refreshInterval = null;
		}

		async connectedCallback() {
			this.render();

			registerRenderCallback(this.slotId, isEmpty => {
				this.isEmpty = isEmpty;
				this.render();
			});

			await visible(this, 0, 100);

			if (this.googleSlot) {
				return;
			}

			googletag.cmd.push(() => {
				const slot = slots[this.slotUnit];

				this.googleSlot = googletag
					.defineSlot(`/9584343/${this.slotUnit}`, slot.sizes, this.slotId)
					.addService(googletag.pubads());

				if (slot.sizeMap) {
					const mapping = slot.sizeMap
						.reduce(
							(mapping, { screen, sizes }) => mapping.addSize(screen, sizes),
							googletag.sizeMapping(),
						)
						.build();
					this.googleSlot.defineSizeMapping(mapping);
				}

				const sizes = slot.sizeMap
					? slot.sizeMap.find(
							({ screen: [width] }) => width <= getMinWidth(getVersion()),
						).sizes
					: slot.sizes;
				const getMaxHeight = sizes => Math.max(...sizes.map(([, y]) => y));
				const maxAdUnitHeight = getMaxHeight(sizes);
				this.style.setProperty("--gpt-slot-min-height", `${maxAdUnitHeight}px`);

				googletag.display(this.slotId);
				this.refreshInterval = setInterval(
					() => googletag.pubads().refresh([this.googleSlot]),
					REFRESH_INTERVAL_SECONDS * 1000,
				);
			});
		}

		disconnectedCallback() {
			stopCheck(this);
			if (this.googleSlot) {
				googletag.cmd.push(() => {
					googletag.destroySlots([this.googleSlot]);
				});
				this.isEmpty = true;
				this.googleSlot = null;
				this.isLoaded = false;
			}
			unregisterRenderCallbacks(this.slotId);
			if (this.refreshInterval) {
				clearInterval(this.refreshInterval);
				this.refreshInterval = null;
			}
		}

		render() {
			render(
				html`<div
					class="${classMap({ "gpt-container-filled": !this.isEmpty || true })}"
				>
					${this.isEmpty
						? nothing
						: html`<div class="gpt-slot-hint">
								${translate("basic_advertisement")}
							</div>`}
					<div
						class="${classMap({
							"gpt-slot": !this.isEmpty,
						})}"
						id="${this.slotId}"
					></div>
				</div>`,
				this,
			);
		}

		get slotId() {
			return this.getAttribute("slot-id");
		}

		get slotUnit() {
			return this.getAttribute("slot-unit");
		}
	},
);
